import { IInput } from '../../../../../types/ComponentTypes/Components'
import { ISearchProduct } from '../../../../../types'
import { useState } from 'react'
import { List, ListItem } from '@sainsburys-tech/luna-list'
import { OutlinedButton } from '@sainsburys-tech/luna-button'
import { ProgressSpinner } from '@sainsburys-tech/luna-progress'

interface ProductSearchComponentProps extends Partial<IInput> {
  products: ISearchProduct[]
  hasMore: boolean
  onSelect?: (product: ISearchProduct) => void
  onLoadMore?: () => void
  scrollThreshold?: number
  isLoadingMore?: boolean
  isLoading?: boolean
}

const PLACEHOLDER_IMAGE_SRC = '/product-placeholder.svg'

function ProductSearchComponent({ products, hasMore, onSelect, onLoadMore, scrollThreshold = 5, isLoadingMore = false, isLoading = false }: ProductSearchComponentProps) {
  const [selectedProduct, setSelectedProduct] = useState<ISearchProduct | null>(null)

  const handleProductSelect = (product: ISearchProduct) => {
    // Update selected product state
    setSelectedProduct(product)
    
    // Notify parent component about the selection
    if (onSelect) {
      onSelect(product)
    }
  }

  return (
    <div className="product-search-container">
        {isLoading && products.length === 0 ? (
          <div className="ln-u-text-align-center ln-u-padding*4" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} aria-live="polite">
            <ProgressSpinner size="medium" />
            <small className="ln-u-margin-top*2 small">Loading products...</small>
          </div>
        ) : products.length > 0 ? (
          <div className="product-search-list" role="region" aria-label="Product search results">
            <div className={products.length > scrollThreshold ? "product-search-list__scroll" : ""}>
                <List type="bare" spaced={false}>
                    {products.map((product) => {
                      const isSelected = selectedProduct?.id
                        ? selectedProduct.id === product.id
                        : selectedProduct === product

                      return (
                      <ListItem key={product.id}>
                          <button 
                              className="product-search-list__item"
                              onClick={() => handleProductSelect(product)}
                              onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleProductSelect(product)
                                }
                              }}
                              type="button"
                              aria-label={`Select ${product.productName}, priced at £${product.price}`}
                              style={{
                                backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                transition: 'background-color 0.2s ease'
                              }}
                              >
                              <img
                                className="product-search-list__image"
                                src={product.image?.trim() ? product.image : PLACEHOLDER_IMAGE_SRC}
                                alt={product.productName}
                                loading="lazy"
                                style={!product.image?.trim() ? { opacity: 0.3, transform: 'scale(0.7)' } : undefined}
                              />
                              <div className="product-search-list__details">
                                  <div className="product-search-list__name">{product.productName}</div>
                                  <div className="product-search-list__price">£{product.price}</div>
                              </div>
                          </button>
                      </ListItem>
                            )
                          })}
                </List>
            </div>
            {hasMore && onLoadMore && (
              <OutlinedButton 
              fullWidth
              onClick={onLoadMore}
              aria-label="Load more products from search results"
              className="ln-u-margin-top*2"
              disabled={isLoadingMore}
              >
                  {isLoadingMore ? 'Loading...' : 'Show more results'}</OutlinedButton>
            )}
          </div>
        ) : null}
    </div>
  )
}

export default ProductSearchComponent